import type { OrderDetailsPath } from '@src/containers/Trading/Markets/OrderDetails/types';
import type { DealBookingType } from '@src/types/OTC';
import type { Column, CustomerQuote, LedgerAccountTypeEnum, MarketDataCardConfig } from '@talos/kyoko';
import type { MultilegComboType } from '../components/MultilegCombo/enums';
import type { PriceBlotterTabProps } from '../containers/Blotters/PriceBlotter/types';
import type { OptionFilter, StrikeFilter } from '../containers/Trading/Markets/Options/Filter/types';

export interface CurrentMarketTabDefaults {
  defaultSubAccountId: number | undefined;
}

export enum MarketTabType {
  Market = 'Market',
  DeepDive = 'DeepDive',
  OrderDetails = 'OrderDetails',
  Option = 'Option',
  Multileg = 'Multileg',
  DealProductBuilder = 'DealProductBuilder',
  ReconDetails = 'ReconDetails',
  AccountLedgerEventsDetails = 'AccountLedgerEventsDetails',
  PriceBlotter = 'Price Grid',
  MultilegPriceBlotter = 'Multileg Price Grid',
  PositionAutoHedging = 'PAH Rules',
  CareOrders = 'Care Orders',
  ManualRFQs = 'Manual RFQs',
}

export interface IMarketTab {
  type: MarketTabType;
  id: string;
  label: string;
  isTemporary?: boolean;
  // temporary tabs can now be save-able or not
  canSave?: boolean;
}

/** @deprecated Use MarketCardPanelConfig instead */
export interface GeneralMarketTab extends IMarketTab {
  type: MarketTabType.Market;
  columns?: MarketDataCardConfig[][];
  defaults?: Partial<CurrentMarketTabDefaults>;
}

/** @deprecated Use DeepDivePanelConfig instead */
export type DeepDiveMarketTabData = {
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  selectedAggregation?: string;
  priceIncrement?: string;
  showMarkets?: boolean;
  showCumQty?: boolean;
  chartResolution?: string;
};

/** @deprecated Use DeepDivePanelConfig instead */
export interface DeepDiveMarketTab extends IMarketTab {
  type: MarketTabType.DeepDive;
  symbol: string;
  data: DeepDiveMarketTabData;
}

/** @deprecated Use PositionAutoHedgingRulesPanelConfig instead */
export interface PositionAutoHedgingTab extends IMarketTab {
  type: MarketTabType.PositionAutoHedging;
}

/** @deprecated Use ReconMismatchesDetailsPanelConfig instead */
export interface ReconDetailsMarketTab extends IMarketTab {
  type: MarketTabType.ReconDetails;
  mismatchIDs: string[];
}

/** @deprecated Use AccountLedgerEventsDetailsPanelConfig instead */
export interface AccountLedgerEventsDetailsMarketTab extends IMarketTab {
  type: MarketTabType.AccountLedgerEventsDetails;
  account: string;
  accountType: LedgerAccountTypeEnum;
  asset?: string;
}

/** @deprecated Use OrderDetailsPanelConfig instead */
export interface OrderDetailsMarketTab extends IMarketTab {
  type: MarketTabType.OrderDetails;
  orderID: string;
  openTab: OrderDetailsPath;
}

export interface CareOrdersTabData {
  // TODO fhqvst We're not entirely sure what will live here yet, so leaving empty
}

/** @deprecated Use CareOrdersPanelConfig instead */
export interface CareOrdersTab extends IMarketTab {
  type: MarketTabType.CareOrders;
  data: CareOrdersTabData;
}

export interface ManualRFQsTabData {
  defaultColumns?: (keyof CustomerQuote | Partial<Column>)[];
  showAllDecimals?: boolean;
}

/** @deprecated Use ManualRFQsPanelConfig instead */
export interface ManualRFQsTab extends IMarketTab {
  type: MarketTabType.ManualRFQs;
  data: ManualRFQsTabData;
}

export type OptionMarketTabData = {
  optionFilter: OptionFilter;
  strikeFilter?: StrikeFilter;
  columnDefsVisibility?: Record<string, boolean>;
};

/** @deprecated Use OptionsPanelConfig instead */
export interface OptionMarketTab extends IMarketTab {
  type: MarketTabType.Option;
  data?: OptionMarketTabData;
}

export type MultilegTabData = {
  instrumentType: MultilegComboType;
  resolvedSymbol?: string;
  legSymbols?: string[];
  legMarketAccounts?: string[];
  legMarkets?: string[];
  initiatingLegs?: boolean[];
  haircutRatios?: string[];
  compactOrderBook?: boolean;
};

/** @deprecated Use MultilegPanelConfig instead */
export interface MultilegMarketTab extends IMarketTab {
  type: MarketTabType.Multileg;
  data: MultilegTabData;
}

export type DealProductBuilderTabData = {
  bookingType: DealBookingType;
  resolvedSymbol?: string;
};

/** @deprecated Use DealProductBuilderPanelConfig instead */
export interface DealProductBuilderMarketTab extends IMarketTab {
  type: MarketTabType.DealProductBuilder;
  data: DealProductBuilderTabData;
}

type PriceBlotterTabData = Partial<Pick<PriceBlotterTabProps, 'defaultColumns' | 'defaultFilter'>>;

/** @deprecated Use PriceGridPanelConfig instead */
export interface PriceBlotterMarketTab extends IMarketTab {
  type: MarketTabType.PriceBlotter;
  data?: PriceBlotterTabData;
}

/** @deprecated Use PanelConfig instead */
export type MarketTab =
  | GeneralMarketTab
  | DeepDiveMarketTab
  | OrderDetailsMarketTab
  | OptionMarketTab
  | MultilegMarketTab
  | DealProductBuilderMarketTab
  | ReconDetailsMarketTab
  | AccountLedgerEventsDetailsMarketTab
  | PriceBlotterMarketTab
  | PositionAutoHedgingTab
  | CareOrdersTab
  | ManualRFQsTab;
